import type {
  AgentPatch,
  EditorCommandPatch,
  EntityMetadataPatch,
  SlateOpsPatch,
} from '@tunasong/agent-tools'
import { getAgentCommandApplicator } from '@tunasong/plugin-lib'
import type { TunaEditor } from '@tunasong/plugin-lib'
import type { Entity } from '@tunasong/schemas'
import { resolveAnchor } from './resolve-anchor.js'

type EditorOperation = Parameters<TunaEditor['apply']>[0]

export const applySlateOps = (editor: TunaEditor, patch: SlateOpsPatch) => {
  for (const op of patch.ops) {
    editor.apply(op as unknown as EditorOperation)
  }
}

export const buildMetadataUpdate = (patch: EntityMetadataPatch): Partial<Entity> => ({ ...patch.fields })

/**
 * Apply an editor-command proposal: walk the live tree to resolve the anchor,
 * then invoke the plugin-registered applicator with the resolved path and args.
 *
 * Stale anchor (`anchor.id` no longer in the tree) → throws so the UI can
 * flip the proposal to a Reject-only stale state rather than mutating the
 * wrong target. Unknown commandId (no applicator registered) → throws,
 * which should not happen in production because run_editor_command also
 * gates on the registry server-side.
 */
export const applyEditorCommand = (editor: TunaEditor, patch: EditorCommandPatch): void => {
  const resolved = resolveAnchor(editor, patch.anchor)
  if ('error' in resolved) {
    throw new Error(resolved.error)
  }
  const applicator = getAgentCommandApplicator(patch.commandId)
  if (!applicator) {
    throw new Error(`No applicator registered for command: ${patch.commandId}`)
  }
  applicator(editor, resolved.path, patch.args ?? {})
}

export interface ApplyProposalDeps {
  editor?: TunaEditor | null
  updateEntity: (entityId: string, partialEntity: Partial<Entity>) => void
}

export const applyProposal = (
  proposal: { targetEntityId: string; patch: AgentPatch },
  deps: ApplyProposalDeps
) => {
  const { patch } = proposal
  if (patch.kind === 'slate-ops') {
    if (!deps.editor) {
      throw new Error('Cannot apply slate-ops proposal: editor is not mounted')
    }
    if (deps.editor.rootElement?.id && deps.editor.rootElement.id !== proposal.targetEntityId) {
      throw new Error(
        `Proposal targets entity ${proposal.targetEntityId} but editor has ${deps.editor.rootElement.id}`
      )
    }
    applySlateOps(deps.editor, patch)
    return
  }
  if (patch.kind === 'entity-metadata') {
    deps.updateEntity(proposal.targetEntityId, buildMetadataUpdate(patch))
    return
  }
  if (patch.kind === 'editor-command') {
    if (!deps.editor) {
      throw new Error('Cannot apply editor-command proposal: editor is not mounted')
    }
    applyEditorCommand(deps.editor, patch)
    return
  }
  const exhaustive: never = patch
  throw new Error(`Unknown patch kind: ${JSON.stringify(exhaustive)}`)
}
